import { Box, Button, Flex, Image, Text, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { FormModal } from '../../components/FormModal'
import { useAppToast } from '../../helpers'
import { cropImage } from '../../helpers/cropImage'
import { acceptAttribute, TFileAccept } from '../FileInput'
import { FormField } from '../FormField'
import { Icon } from '../Icon'

interface IImageCropInputProps {
  /** File (novo) ou URL string (existente) para preview inicial */
  value?: File | string | null
  /** Callback com o File já recortado */
  onChange: (file: File) => void
  /** Proporção fixa obrigatória (ex: 375/221). Omitir para proporção livre. */
  aspectRatio?: number
  /** Formatos aceitos. Sai do registro de `components/FileInput` — `image` cobre o painel inteiro. */
  accept?: TFileAccept
  /** Tamanho máximo em MB */
  maxSizeMB?: number
  /** Lado máximo do recorte, em px. O resultado é reduzido até caber e sai em JPEG. */
  maxOutputSize?: number
  /** Texto exibido abaixo do preview (ex: "375px x 221px") */
  suggestedSize?: string
  /** CSS aspect-ratio para o container de preview (ex: "375 / 221") */
  previewAspectRatio?: string
  /** Altura fixa do preview (alternativa ao previewAspectRatio) */
  previewHeight?: string
  /** Rótulo do campo. Sem ele, o controle sai sem rótulo. */
  label?: string
  /** Obrigatoriedade: o asterisco vem do `FormField`, não do texto do rótulo. */
  isRequired?: boolean
  isDisabled?: boolean
  /** Mensagens de erro do backend */
  error?: string[]
}

export default function ImageCropInput({
  value,
  onChange,
  aspectRatio,
  accept = 'image',
  maxSizeMB,
  maxOutputSize,
  suggestedSize,
  previewAspectRatio,
  previewHeight,
  label,
  isRequired = false,
  isDisabled = false,
  error
}: IImageCropInputProps) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useAppToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [srcForCrop, setSrcForCrop] = useState<string | null>(null)
  const [originalFileName, setOriginalFileName] = useState<string>('image.jpg')
  const [originalMimeType, setOriginalMimeType] = useState<string>('image/jpeg')
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const imgRef = useRef<HTMLImageElement>(null)
  const [isCropping, setIsCropping] = useState(false)

  // Preview: se value é File → objectURL, se string → URL direta
  const previewSrc = useMemo(() => {
    if (value instanceof File) return URL.createObjectURL(value)
    return value || null
  }, [value])

  useEffect(() => {
    return () => {
      if (previewSrc?.startsWith('blob:')) URL.revokeObjectURL(previewSrc)
    }
  }, [previewSrc])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`A imagem não pode ter mais de ${maxSizeMB}MB.`)
      e.target.value = ''
      return
    }

    setOriginalFileName(file.name)
    setOriginalMimeType(file.type || 'image/jpeg')
    setCrop(undefined)
    setCompletedCrop(undefined)

    const reader = new FileReader()
    reader.onload = () => {
      setSrcForCrop(reader.result as string)
      onOpen()
    }
    reader.readAsDataURL(file)

    e.target.value = ''
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget

    if (aspectRatio) {
      const initialCrop = centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspectRatio, naturalWidth, naturalHeight),
        naturalWidth,
        naturalHeight
      )
      setCrop(initialCrop)
    } else {
      setCrop({ unit: '%', x: 5, y: 5, width: 90, height: 90 })
    }
  }

  const handleConfirmCrop = async () => {
    if (!completedCrop || !imgRef.current || !srcForCrop) return
    if (completedCrop.width === 0 || completedCrop.height === 0) return

    const img = imgRef.current
    const scaleX = img.naturalWidth / img.width
    const scaleY = img.naturalHeight / img.height

    const naturalCrop: PixelCrop = {
      unit: 'px',
      x: Math.round(completedCrop.x * scaleX),
      y: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY)
    }

    setIsCropping(true)
    try {
      const croppedFile = await cropImage(
        srcForCrop,
        naturalCrop,
        originalFileName,
        originalMimeType,
        maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined,
        maxOutputSize
      )
      onChange(croppedFile)
      setSrcForCrop(null)
      setCrop(undefined)
      setCompletedCrop(undefined)
      onClose()
    } catch {
      toast.error('Erro ao recortar a imagem. Tente novamente.')
    } finally {
      setIsCropping(false)
    }
  }

  const handleModalClose = () => {
    setCrop(undefined)
    setCompletedCrop(undefined)
    setSrcForCrop(null)
    onClose()
  }

  return (
    <>
      {/* O campo inteiro passa pelo `FormField`: rótulo, obrigatoriedade e erro seguem a mesma
          convenção do resto do painel. O modal de recorte fica fora dele, porque não é o campo. */}
      <FormField label={label} error={error} isRequired={isRequired} isDisabled={isDisabled}>
        {/* Preview area */}
        <Box
          border="2px dashed"
          borderColor="gray.300"
          borderRadius="md"
          overflow="hidden"
          bg="gray.50"
          {...(previewHeight
            ? { height: previewHeight }
            : previewAspectRatio
              ? { aspectRatio: previewAspectRatio }
              : { minH: '100px' })}
        >
          {previewSrc ? (
            <Image src={previewSrc} alt="Preview" width="100%" height="100%" objectFit="contain" />
          ) : (
            <Flex align="center" justify="center" w="100%" h="100%" minH="100px" color="gray.400">
              <Icon name="lni lni-image" fontSize="40px" />
            </Flex>
          )}
        </Box>

        {suggestedSize && (
          <Text variant="caption" mt={1}>
            <strong>Tamanho Sugerido:</strong> {suggestedSize}.
          </Text>
        )}

        {!isDisabled && (
          <>
            <Button mt={2} size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
              {previewSrc ? 'Alterar imagem' : 'Selecionar imagem'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept={acceptAttribute(accept)}
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />
          </>
        )}
      </FormField>

      {/* Crop Modal */}
      <FormModal
        isOpen={isOpen}
        onClose={handleModalClose}
        title={
          <>
            Recortar imagem
            {aspectRatio && (
              <Text variant="caption" mt={1}>
                Proporção obrigatória: {suggestedSize || aspectRatio.toFixed(2)}
              </Text>
            )}
          </>
        }
        size="wide"
        footer={
          <>
            <Button variant="ghost" onClick={handleModalClose} isDisabled={isCropping}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmCrop}
              isLoading={isCropping}
              isDisabled={!completedCrop || completedCrop.width === 0}
            >
              Recortar
            </Button>
          </>
        }
      >
        {srcForCrop && (
          <Flex justify="center" align="center">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
            >
              <img
                ref={imgRef}
                src={srcForCrop}
                alt="Recortar"
                onLoad={handleImageLoad}
                style={{ maxHeight: '60vh', maxWidth: '100%', display: 'block' }}
              />
            </ReactCrop>
          </Flex>
        )}
      </FormModal>
    </>
  )
}
